import classNames from 'classnames';
import { useEventTypeApi } from 'modules/api';
import { AuthShowContainer } from 'modules/auth';
import {
    EventTypeForm,
    useEventTypes,
    useMutateEventTypes,
} from 'modules/event-types';
import { useI18n } from 'modules/i18n';
import { useInvalidateAllPersonData } from 'modules/person';
import { useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { InlineNotification } from 'modules/ui';
import { Helmet } from 'react-helmet';

import { AddButton, AdminRecord, EditViewOrRedirect } from '../components';

/**
 * Lists event types using current project details for scoped editing controls.
 * @returns {React.ReactElement} Event type administration or project loading/error state.
 */
export default function EventTypesAdminPage() {
    const { t, locale } = useI18n();
    const {
        project,
        isLoading: projectLoading,
        error: projectError,
    } = useProject();
    const {
        data,
        isLoading,
        isValidating,
        error: eventTypesError,
    } = useEventTypes();
    const mutateEventTypes = useMutateEventTypes();
    const { deleteEventType } = useEventTypeApi();
    const invalidateAllPersonData = useInvalidateAllPersonData();

    const scope = 'event_type';
    const hideAdd = false,
        hideEdit = false,
        hideDelete = false;
    const detailsAttributes = ['code', 'name'];
    const outerScope = 'project';
    const outerScopeId = project?.id;
    const joinedData = {};
    const showComponent = undefined;

    const requestError = projectError || eventTypesError;
    if (requestError)
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={requestError.message}
                isClosable={false}
            />
        );
    if (isLoading || projectLoading || !project || !data) {
        return (
            <div className="wrapper-content register">
                <Spinner />
            </div>
        );
    }

    function renderForm(data, onSubmit, onCancel) {
        return (
            <EventTypeForm
                data={data}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        );
    }

    async function handleDeleteEventType(id, callback) {
        mutateEventTypes(async (eventTypes) => {
            await deleteEventType(id);

            await invalidateAllPersonData();

            const updatedEventTypes = eventTypes.filter((et) => et.id !== id);
            return updatedEventTypes;
        });

        if (typeof callback === 'function') {
            callback();
        }
    }

    const sortedData = Object.values(data).sort((a, b) => {
        const aName = a.name;
        const bName = b.name;
        return aName.localeCompare(bName, locale);
    });

    return (
        <EditViewOrRedirect>
            <div className="wrapper-content register">
                <Helmet>
                    <title>{t(`activerecord.models.${scope}.other`)}</title>
                </Helmet>

                <AuthShowContainer hasProjectAccess>
                    <h1 className="Page-main-title">
                        {t(`activerecord.models.${scope}.other`)}
                    </h1>

                    <div
                        className={classNames('LoadingOverlay', {
                            'is-loading': isValidating,
                        })}
                    >
                        {!hideAdd && (
                            <AddButton
                                scope={scope}
                                onClose={(closeModal) =>
                                    renderForm(
                                        undefined,
                                        closeModal,
                                        closeModal
                                    )
                                }
                                disabled={isValidating}
                            />
                        )}

                        {sortedData.map((data) => (
                            <AdminRecord
                                data={data}
                                scope={scope}
                                outerScope={outerScope}
                                outerScopeId={outerScopeId}
                                detailsAttributes={detailsAttributes}
                                joinedData={joinedData}
                                form={renderForm}
                                showComponent={showComponent}
                                hideEdit={hideEdit}
                                hideDelete={hideDelete}
                                handleDelete={handleDeleteEventType}
                                disabled={isValidating}
                                key={`${scope}-${data.id}`}
                            />
                        ))}

                        {!hideAdd && (
                            <AddButton
                                scope={scope}
                                onClose={(closeModal) =>
                                    renderForm(
                                        undefined,
                                        closeModal,
                                        closeModal
                                    )
                                }
                                disabled={isValidating}
                            />
                        )}
                    </div>
                </AuthShowContainer>

                <AuthShowContainer ifLoggedOut ifNoProject>
                    {t('devise.failure.unauthenticated')}
                </AuthShowContainer>
            </div>
        </EditViewOrRedirect>
    );
}
